/**
 * injectStepProductIds
 *
 * Distributes the step-level catalog product IDs across the template's
 * `product-showcase` blocks so a single multi-product selection on the step can
 * populate one or more showcase grids.
 *
 * Each showcase block consumes up to `columns` products (the same value the
 * renderer uses to cap the grid, defaulting to 3). Blocks are filled in document
 * order, so the chosen products flow left-to-right, top-to-bottom across every
 * showcase in the template. Blocks that fall outside the selection keep whatever
 * `product_ids` were authored into the template.
 */

import type { StepPageBlock, StepPageContent } from '../types';

const PRODUCT_SHOWCASE_TYPE = 'product-showcase';
const DEFAULT_SHOWCASE_COLUMNS = 3;

function readColumns(block: StepPageBlock): number {
  const raw = block.props?.columns;
  const parsed = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(parsed) || parsed < 1) return DEFAULT_SHOWCASE_COLUMNS;
  return Math.floor(parsed);
}

function distributeAcrossBlocks(
  blocks: StepPageBlock[],
  productIds: string[],
): StepPageBlock[] {
  let cursor = 0;
  return blocks.map((block) => {
    if (block.type !== PRODUCT_SHOWCASE_TYPE || cursor >= productIds.length) {
      return block;
    }
    const columns = readColumns(block);
    const slice = productIds.slice(cursor, cursor + columns);
    cursor += columns;
    if (slice.length === 0) return block;
    return {
      ...block,
      props: {
        ...(block.props ?? {}),
        product_ids: slice,
      },
    };
  });
}

export function injectStepProductIds(
  pageContent: StepPageContent | StepPageBlock[] | null,
  productIds: string[] | undefined | null,
): StepPageContent | StepPageBlock[] | null {
  if (!pageContent || !productIds || productIds.length === 0) {
    return pageContent;
  }

  if (Array.isArray(pageContent)) {
    return distributeAcrossBlocks(pageContent, productIds);
  }

  if (Array.isArray(pageContent.blocks)) {
    return { ...pageContent, blocks: distributeAcrossBlocks(pageContent.blocks, productIds) };
  }

  if (Array.isArray(pageContent.components)) {
    return { ...pageContent, components: distributeAcrossBlocks(pageContent.components, productIds) };
  }

  return pageContent;
}
